import { useState, useEffect, useContext } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import request from "../../utilities/requests";
import {
  Typography,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Link,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import PaymentDialog from "./components/PaymentDialog";
import DisputeDialog from "./components/DisputeDialog";
import MarkPaidDialog from "./components/MarkPaidDialog";
import BillingNotifications from "./components/BillingNotifications";
import { EventContext } from "../../contexts/EventContext";
import { toast } from "../../utilities/SnackBarUtils";

const InvoiceDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userData } = useContext(EventContext);
  const { id: invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputed, setDisputed] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const [editableItems, setEditableItems] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [disableBtns, setDisableBtns] = useState(false);
  const [editableStatus, setEditableStatus] = useState("");
  const [editableDueDate, setEditableDueDate] = useState("");
  const [editablePeriodStart, setEditablePeriodStart] = useState("");
  const [editablePeriodEnd, setEditablePeriodEnd] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState(null);

  // Mark as Paid dialog state
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  useEffect(() => {
    if (invoice?.status === "disputed") {
      setDisputed(true);
    }
    if (["paid", "pending"].includes(invoice?.status)) {
      setDisableBtns(true);
    }
  }, [invoice]);

  useEffect(() => {
    if (userData?.role === "superadmin") {
      setCanEdit(true);
    }
  }, [userData]);

  useEffect(() => {
    if (invoice && !["pending", "paid"].includes(invoice?.status)) {
      const pi = searchParams.get("payment_intent");
      if (pi) {
        setPaymentIntentId(pi);
        getPayment(pi);
      }
    }
  }, [invoice, searchParams]);

  const fetchInvoice = async () => {
    const result = await request(`contracts/invoice/?id=${invoiceId}`);
    if (result.success) {
      setInvoice(result.invoice);
      setEditableItems(
        result.invoice.items.map((item) => ({
          ...item,
          description:
            item.description || `${item.quantity} attendees @ $${item.price}`,
          quantity: item.quantity,
          price: item.price,
          currency: item.currency || "usd",
        })),
      );
      setEditableStatus(result.invoice.status);
      setEditableDueDate(
        new Date(result.invoice.dueDate).toISOString().substr(0, 10),
      );
      if (result.invoice.periodStart) {
        setEditablePeriodStart(
          new Date(result.invoice.periodStart).toISOString().substr(0, 10),
        );
      }
      if (result.invoice.periodEnd) {
        setEditablePeriodEnd(
          new Date(result.invoice.periodEnd).toISOString().substr(0, 10),
        );
      }
      setIsDirty(false);
      setDisputed(result.invoice.status === "disputed");
    }
  };

  const saveChanges = async () => {
    const payload = {
      items: editableItems,
      status: editableStatus,
      dueDate: editableDueDate,
      periodStart: editablePeriodStart,
      periodEnd: editablePeriodEnd,
    };
    const result = await request(
      `contracts/invoice/update/${invoiceId}`,
      "post",
      payload,
    );
    if (result.success) {
      fetchInvoice();
      toast.success("Invoice updated");
    } else {
      toast.error(result.error);
    }
  };

  const getPayment = async (pi) => {
    const body = { paymentIntentId: pi };
    const result = await request(`contracts/invoice/syncinvoice`, "post", body);
    if (result.success) {
      fetchInvoice();
      toast.success(result.message);
    } else {
      toast.error(result.error);
    }
  };

  const handleMarkPaidClose = () => {
    setMarkPaidOpen(false);
    fetchInvoice();
  };

  const handleRecalculate = async () => {
    if (!canEdit) return;
    const result = await request(
      `contracts/invoice/calculate/${invoice?.event?._id}`,
    );
    if (result.success) {
      setInvoice(result.invoiceDoc);
      toast.success("Invoice calculation updated");
    }
  };

  return (
    <Grid container spacing={2} sx={{ padding: "24px" }}>
      {/* Header info */}
      <Grid item xs={12} onClick={() => navigate(-1)}>
        <Button size="small">back to invoices</Button>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">Invoice Detail</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2">Invoice #</Typography>
        <Typography variant="body2">{invoice?._id}</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2">Status</Typography>
        {canEdit ? (
          <Select
            fullWidth
            size="small"
            value={editableStatus}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "paid" && userData.role === "superadmin") {
                setMarkPaidOpen(true);
              }
              setEditableStatus(val);
              setIsDirty(true);
            }}
          >
            {["open", "pending", "paid", "disputed"].map((s) => (
              <MenuItem key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Typography variant="body1">{invoice?.status}</Typography>
        )}
        {((editedStatus) =>
          editedStatus === "disputed" ? (
            <>
              <Typography variant="subtitle2">Dispute Reason</Typography>
              <Typography variant="body1">{invoice?.disputeReason}</Typography>
            </>
          ) : null)(editableStatus)}
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2">Organization</Typography>
        <Typography variant="body1">{invoice?.org.name}</Typography>
      </Grid>
      {invoice?.event && (
        <Grid item xs={12} sm={6}>
          <>
            {" "}
            <Typography variant="subtitle2">Event</Typography>
            <Typography variant="body1">{invoice?.event?.name}</Typography>
          </>
        </Grid>
      )}
      {invoice?.event && (
        <Grid item xs={12} sm={6}>
          <>
            <Typography variant="subtitle2">Event Date</Typography>
            <Typography variant="body1">
              {new Date(invoice?.event?.date).toLocaleString()}
            </Typography>
          </>
        </Grid>
      )}
      {!invoice?.event && (
        <Grid item sm={6} xs={12}>
          <Typography variant="subtitle2">Period Start</Typography>
          {canEdit ? (
            <TextField
              type="date"
              fullWidth
              size="small"
              value={editablePeriodStart}
              onChange={(e) => {
                setEditablePeriodStart(e.target.value);
                setIsDirty(true);
              }}
            />
          ) : (
            <Typography variant="body1">
              {new Date(invoice?.periodStart).toLocaleDateString()}
            </Typography>
          )}
        </Grid>
      )}

      {!invoice?.event && (
        <Grid item sm={6} xs={12}>
          <Typography variant="subtitle2">Period End</Typography>
          {canEdit ? (
            <TextField
              type="date"
              fullWidth
              size="small"
              value={editablePeriodEnd}
              onChange={(e) => {
                setEditablePeriodEnd(e.target.value);
                setIsDirty(true);
              }}
            />
          ) : (
            <Typography variant="body1">
              {new Date(invoice?.periodEnd).toLocaleDateString()}
            </Typography>
          )}
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle2">Due Date</Typography>
        {canEdit ? (
          <TextField
            type="date"
            fullWidth
            size="small"
            value={editableDueDate}
            onChange={(e) => {
              setEditableDueDate(e.target.value);
              setIsDirty(true);
            }}
          />
        ) : (
          <Typography variant="body1">
            {new Date(invoice?.dueDate).toLocaleDateString()}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Line Items
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {editableItems.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {canEdit ? (
                    <TextField
                      fullWidth
                      value={item.description}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditableItems((prev) => {
                          return prev.map((it, i) =>
                            i === idx ? { ...it, description: val } : it,
                          );
                        });
                        setIsDirty(true);
                      }}
                      size="small"
                    />
                  ) : (
                    item.description
                  )}
                </TableCell>
                <TableCell align="right">
                  {canEdit ? (
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10) || 0;
                        setEditableItems((prev) => {
                          return prev.map((it, i) =>
                            i === idx
                              ? {
                                  ...it,
                                  quantity: val,
                                  description:
                                    it.description ===
                                    `${it.quantity} attendees @ $${it.price}`
                                      ? `${val} attendees @ $${it.price}`
                                      : it.description,
                                }
                              : it,
                          );
                        });
                        setIsDirty(true);
                      }}
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  ) : (
                    item.quantity
                  )}
                </TableCell>
                <TableCell align="right">
                  {canEdit ? (
                    <TextField
                      type="number"
                      value={item.price}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setEditableItems((prev) => {
                          return prev.map((it, i) =>
                            i === idx
                              ? {
                                  ...it,
                                  price: val,
                                  description:
                                    it.description ===
                                    `${it.quantity} attendees @ $${it.price}`
                                      ? `${it.quantity} attendees @ $${val}`
                                      : it.description,
                                }
                              : it,
                          );
                        });
                        setIsDirty(true);
                      }}
                      inputProps={{ min: 0, step: "0.01" }}
                      size="small"
                    />
                  ) : (
                    `${item.currency.toUpperCase()} ${item.price.toFixed(2)}`
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.currency.toUpperCase()}{" "}
                  {(item.price * item.quantity).toFixed(2)}
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setEditableItems((prev) =>
                        prev.filter((_, i) => i !== idx),
                      );
                      setIsDirty(true);
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={4}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditableItems((prev) => [
                      ...prev,
                      {
                        description: "",
                        quantity: 1,
                        price: 0,
                        currency: invoice.currency || "usd",
                      },
                    ]);
                    setIsDirty(true);
                  }}
                >
                  Add Item
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Grid>
      {canEdit && isDirty && (
        <Grid item xs={12} sx={{ textAlign: "right", mb: 2 }}>
          <Button variant="contained" onClick={saveChanges}>
            Save Changes
          </Button>
        </Grid>
      )}
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2">Amount Due</Typography>
        <Typography variant="body1">
          {invoice?.currency.toUpperCase()} {invoice?.amountDue.toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2">Amount Paid</Typography>
        <Typography variant="body1">
          {invoice?.currency.toUpperCase()} {invoice?.amountPaid.toFixed(2)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="subtitle2">Paid At</Typography>
        <Typography variant="body1">
          {invoice?.paidAt ? new Date(invoice?.paidAt).toLocaleString() : "-"}
        </Typography>
      </Grid>
      {invoice?.notes?.length > 0 && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Notes</Typography>
          {invoice.notes.map((note, idx) => (
            <div key={idx} style={{ marginBottom: "8px" }}>
              <Typography variant="body2">{note.message}</Typography>
              <Typography variant="caption" color="textSecondary">
                {note.createdAt
                  ? new Date(note.createdAt).toLocaleString()
                  : ""}{" "}
                {note.createdBy ? `by ${note.createdBy}` : ""}
              </Typography>
            </div>
          ))}
        </Grid>
      )}
      {invoice?.invoicePdf && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            component={Link}
            href={invoice?.invoicePdf}
            target="_blank"
          >
            Download PDF
          </Button>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        style={{
          marginTop: 30,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          alignItems: "center",
        }}
      >
        <Typography variant="button">Pay Now with:</Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            justifyContent: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setPaymentMethod("ach");
              setDialogOpen(true);
            }}
            disabled={disableBtns}
          >
            ACH
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setPaymentMethod("card");
              setDialogOpen(true);
            }}
            disabled={disableBtns}
          >
            Credit Card
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setPaymentMethod("zelle");
              setDialogOpen(true);
            }}
            disabled={disableBtns}
          >
            Zelle
          </Button>
        </div>
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          marginTop: 10,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          alignItems: "center",
        }}
      >
        <Typography variant="button">Something not look right?</Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 10,
            justifyContent: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDisputeOpen(true)}
            disabled={disputed || disableBtns}
          >
            dispute
          </Button>
        </div>
      </Grid>
      {canEdit ? (
        <Grid item xs={12}>
          <Button variant="contained" size="small" onClick={handleRecalculate}>
            Recalculate
          </Button>
        </Grid>
      ) : null}
      {canEdit ? <BillingNotifications invoiceId={invoiceId} /> : null}
      <PaymentDialog
        open={dialogOpen}
        close={() => setDialogOpen(false)}
        invoiceId={invoiceId}
        method={paymentMethod}
      />
      <DisputeDialog
        open={disputeOpen}
        close={() => {
          fetchInvoice();
          setDisputeOpen(false);
        }}
        invoiceId={invoiceId}
      />
      <MarkPaidDialog
        open={markPaidOpen}
        close={handleMarkPaidClose}
        invoiceId={invoiceId}
      />
      <div style={{ height: 100 }} />
    </Grid>
  );
};

export default InvoiceDetail;
